import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import useRankingUser from "./hooks/useRankingUser";
import { EmptyState } from "@/core/ui";

const POSITION_COLORS = {
  1: "#efb810",
  2: "#90a4ae",
  3: "#a66a43",
};

const StatItem = ({ label, value, color }) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontWeight: 600,
        fontSize: "0.65rem",
        lineHeight: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: "0.82rem",
        color: color ?? "text.primary",
        lineHeight: 1.3,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const PlayerRow = ({ player }) => {
  const isCurrentUser = Boolean(player.isCurrentUser);
  const posColor = POSITION_COLORS[player.position] ?? null;
  const saldoColor =
    player.saldo > 0
      ? "success.main"
      : player.saldo < 0
        ? "error.main"
        : "text.secondary";
  const saldoLabel =
    player.saldo > 0 ? `+${player.saldo}` : String(player.saldo);

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.2,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: isCurrentUser ? "primary.main" : "divider",
        backgroundColor: isCurrentUser ? "action.selected" : "transparent",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Typography
          sx={{
            minWidth: 22,
            fontWeight: 900,
            fontSize: "0.9rem",
            color: posColor ?? "text.secondary",
            flexShrink: 0,
          }}
        >
          {player.position}
        </Typography>

        <Avatar
          src={player.avatarUrl || undefined}
          sx={{
            width: 36,
            height: 36,
            bgcolor: posColor ?? "action.selected",
            flexShrink: 0,
          }}
        >
          {player.name[0]}
        </Avatar>

        <Typography
          noWrap
          sx={{
            fontWeight: isCurrentUser ? 800 : 700,
            fontSize: "0.9rem",
            flex: 1,
            minWidth: 0,
          }}
        >
          {isCurrentUser ? `${player.name} (você)` : player.name}
        </Typography>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "0.95rem",
            flexShrink: 0,
            ml: "auto",
          }}
        >
          {player.points} pts
        </Typography>
      </Stack>

      <Divider sx={{ my: 0.8 }} />

      <Stack direction="row" justifyContent="space-around" sx={{ pl: "58px" }}>
        <StatItem label="PJ" value={player.matchesPlayed} />
        <StatItem label="V" value={player.wins} color="success.main" />
        <StatItem label="D" value={player.losses} color="error.main" />
        <StatItem label="Saldo" value={saldoLabel} color={saldoColor} />
      </Stack>
    </Paper>
  );
};

const RankingUser = () => {
  const {
    initialize,
    rankingsData,
    categoriesData,
    selectedCategoryByRankingId,
    setCategoryForRanking,
    getStandingsForRanking,
    getStandings,
    loading,
    error,
  } = useRankingUser();

  // `undefined` means "not decided yet" (first load); `null` means "collapsed by user"
  const [expandedRanking, setExpandedRanking] = useState(undefined);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (rankingsData.length > 0 && expandedRanking === undefined) {
      setExpandedRanking(rankingsData[0].id);
    }
  }, [expandedRanking, rankingsData]);

  const handleExpand = useCallback(
    (rankingId) => (_, isExpanded) => {
      setExpandedRanking(isExpanded ? rankingId : null);
    },
    [],
  );

  useEffect(() => {
    if (!expandedRanking) return;
    const categoryId = selectedCategoryByRankingId[expandedRanking] ?? null;
    getStandings(expandedRanking, categoryId);
  }, [expandedRanking, selectedCategoryByRankingId, getStandings]);

  const handleCategoryChange = useCallback(
    (rankingId) => (event) => {
      console.log(
        "Categoria selecionada para ranking",
        rankingId,
        event.target.value,
      );
      const categoryId = event.target.value;
      setCategoryForRanking(rankingId, categoryId);
    },
    [setCategoryForRanking],
  );

  const handleCategoryItemClick = useCallback(
    (rankingId, categoryId) => () => {
      setCategoryForRanking(rankingId, categoryId);
    },
    [setCategoryForRanking],
  );

  const categoryLabelById = useMemo(() => {
    const map = new Map();
    categoriesData.forEach((category) => {
      map.set(category.id, `${category.name}`);
    });
    return map;
  }, [categoriesData]);

  const isLoading = loading.rankings;
  const isWithoutSeason = !isLoading && rankingsData.length === 0;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Ranking
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 600 }}
        >
          Todos jogadores
        </Typography>
      </Box>

      {error?.message ? (
        <Typography variant="body2" color="error">
          {error.message}
        </Typography>
      ) : null}

      {isWithoutSeason ? (
        <EmptyState
          icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 34 }} />}
          title="Você ainda não está em nenhum ranking"
          description="Assim que você for inscrito em uma temporada, a classificação aparecerá aqui."
          sx={{ py: 8 }}
        />
      ) : null}

      {isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={56} />
          <Skeleton variant="rounded" height={56} />
        </Stack>
      ) : null}

      {rankingsData.map((ranking) => {
        const selectedCategoryId =
          selectedCategoryByRankingId[ranking.id] ?? "";
        const players = getStandingsForRanking(ranking.id, selectedCategoryId);
        const title = ranking.year
          ? `${ranking.name} • ${ranking.year}`
          : ranking.name;
        const selectedCategoryLabel =
          selectedCategoryId && categoryLabelById.get(selectedCategoryId)
            ? categoryLabelById.get(selectedCategoryId)
            : "Selecione a categoria";

        return (
          <Accordion
            key={ranking.id}
            expanded={expandedRanking === ranking.id}
            onChange={handleExpand(ranking.id)}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 2, py: 1, "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 2, pb: 2, pt: 1 }}>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedCategoryId}
                      displayEmpty
                      renderValue={() => selectedCategoryLabel}
                      onChange={handleCategoryChange(ranking.id)}
                    >
                      {categoriesData.length === 0 ? (
                        <MenuItem value="" disabled>
                          Nenhuma categoria disponível
                        </MenuItem>
                      ) : (
                        <>
                          <MenuItem value="" disabled>
                            Selecione a categoria
                          </MenuItem>
                          {categoriesData.map((category) => (
                            <MenuItem
                              key={category.id}
                              value={category.id}
                              onClick={handleCategoryItemClick(
                                ranking.id,
                                category.id,
                              )}
                            >
                              {category.name}
                            </MenuItem>
                          ))}
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {expandedRanking === ranking.id && loading.players ? (
                <Stack spacing={1}>
                  <Skeleton variant="rounded" height={72} />
                  <Skeleton variant="rounded" height={72} />
                  <Skeleton variant="rounded" height={72} />
                </Stack>
              ) : null}

              {!loading.players && players.length === 0 ? (
                <EmptyState
                  icon={<EmojiEventsOutlinedIcon sx={{ fontSize: 34 }} />}
                  title="Nenhum jogador encontrado"
                  description="Nenhum jogador encontrado para esta categoria."
                  sx={{ py: 2 }}
                />
              ) : null}

              {players.length > 0 && (
                <Stack spacing={1}>
                  {players.map((player) => (
                    <PlayerRow key={player.id} player={player} />
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default RankingUser;
